/** Parse ScriptComponent / quote-formatted price (e.g. en-IN commas) to a finite number for API payloads. */
export function parsePriceToNumber(raw) {
  if (raw == null || raw === "") return 0;
  const s = String(raw).replace(/,/g, "").trim();
  if (s === "") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

export function fmtQuoteValue(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n) || !Number.isFinite(n)) return String(v);
  if (Number.isInteger(n) && Math.abs(n) >= 1000) {
    return n.toLocaleString("en-IN");
  }
  if (!Number.isInteger(n)) {
    return n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }
  return String(n);
}

/** Circuit bound for limit-price clamping: null means skip that side (missing, non-finite, or zero). */
export function parseCircuitLimitBound(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(String(raw).replace(/,/g, "").trim());
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

export const LIMIT_ORDER_UI_LABEL = "Limit Order";

export function isLimitOrderUiType(orderType) {
  return String(orderType ?? "").trim() === LIMIT_ORDER_UI_LABEL;
}

/**
 * When order type is Limit Order, clamp parsed price to circuit range; omit sides with no bound.
 * Returns the original string if unchanged (preserves user formatting when already in range).
 */
export function clampPriceToCircuitLimits(priceStr, orderType, lowerCircuit, upperCircuit) {
  if (!isLimitOrderUiType(orderType)) return priceStr;
  if (priceStr == null) return priceStr;
  const s = String(priceStr).trim();
  if (s === "") return "";

  const n = parsePriceToNumber(s);
  let minB = lowerCircuit == null ? null : lowerCircuit;
  let maxB = upperCircuit == null ? null : upperCircuit;
  if (minB != null && maxB != null && minB > maxB) {
    return priceStr;
  }

  let x = n;
  if (minB != null) x = Math.max(minB, x);
  if (maxB != null) x = Math.min(maxB, x);
  if (x === n) return priceStr;
  return fmtQuoteValue(x);
}

export function mapQuoteDataToScriptProps(quoteItem) {
  if (!quoteItem) return {};
  const {
    depth,
    ohlc,
    last_price,
    pct_change,
    average_price,
    volume,
    underlying_last_price,
    lower_circuit_limit,
    upper_circuit_limit,
    oi,
    last_quantity,
    last_trade_time,
    buy_quantity,
    sell_quantity,
    tradingsymbol,
    lot_size,
  } = quoteItem;
  const pctStr = pct_change != null ? `${pct_change >= 0 ? "+" : ""}${pct_change.toFixed(2)}%` : "—";
  const fmt = (v) => fmtQuoteValue(v);
  return {
    headerSymbol: tradingsymbol ?? "",
    marginRequired: "",
    quoteMetrics: [
      { value: fmt(last_price), label: "LTP" },
      { value: fmt(last_quantity), label: "LT Qty" },
      { value: pctStr, label: "% Chg", positive: pct_change >= 0 },
      { value: fmt(average_price), label: "ATP" },
      { value: fmt(volume), label: "Volume" },
      { value: fmt(underlying_last_price), label: "Und LTP" },
      { value: pctStr, label: "Net chg", positive: pct_change >= 0 },
      { value: fmt(ohlc?.open), label: "Open" },
      { value: fmt(ohlc?.high), label: "High" },
      { value: fmt(ohlc?.low), label: "Low" },
      { value: fmt(ohlc?.close), label: "Close" },
      { value: fmt(lower_circuit_limit), label: "Low ckt" },
      { value: fmt(upper_circuit_limit), label: "Up ckt" },
      { value: fmt(oi), label: "OI" },
    ],
    orderBookBuyRows: (depth?.buy ?? []).slice(0, 5).map((row) => ({
      order: row.orders != null && row.orders !== "" ? fmt(row.orders) : "—",
      quantity: fmt(row.quantity),
      price: row.price != null && row.price !== "" ? fmt(row.price) : "",
    })),
    orderBookSellRows: (depth?.sell ?? []).slice(0, 5).map((row) => ({
      order: row.orders != null && row.orders !== "" ? fmt(row.orders) : "—",
      quantity: fmt(row.quantity),
      price: row.price != null && row.price !== "" ? fmt(row.price) : "",
    })),
    bidFooter: [
      { label: "TBQ", value: fmt(buy_quantity) },
      { label: "LTT", value: last_trade_time ?? "—" },
    ],
    askFooter: [
      { label: "LSQ", value: fmt(sell_quantity) },
      { label: "LUT", value: last_trade_time ?? "—" },
    ],
    defaultPrice: last_price != null ? fmt(last_price) : "",
    defaultQtyLots: "",
    defaultAbsDisQty: "",
    quoteLotSize: lot_size != null && lot_size !== "" ? Number(lot_size) : undefined,
    lowerCircuitLimit: parseCircuitLimitBound(lower_circuit_limit),
    upperCircuitLimit: parseCircuitLimitBound(upper_circuit_limit),
  };
}

export function apiExpiryToDisplay(apiDate) {
  if (!apiDate || typeof apiDate !== "string") return "";
  const [y, m, d] = apiDate.split("-");
  return d && m && y ? `${d}-${m}-${y}` : apiDate;
}

export function displayExpiryToApi(display) {
  if (!display || typeof display !== "string") return "";
  const parts = display.trim().split("-");
  if (parts.length === 3 && parts[0].length <= 2 && parts[1].length <= 2) {
    const [d, m, y] = parts;
    return `${y}-${m}-${d}`;
  }
  return display;
}

export function formatAvailableMargin(val) {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

const INFINITY_THRESHOLD = 1e10;

export function isScripInfinite(val) {
  if (val == null || val === "") return false;
  const n = typeof val === "number" ? val : Number(String(val).replace(/,/g, "").trim());
  if (n === Infinity || n === -Infinity) return true;
  return Number.isFinite(n) && Math.abs(n) > INFINITY_THRESHOLD;
}

export function parseScripNumeric(val) {
  if (val == null || val === "") return NaN;
  if (typeof val === "number") return val;
  return Number(String(val).replace(/,/g, "").trim());
}

export function formatScripValue(val) {
  if (val == null) return "—";
  const n = parseScripNumeric(val);
  if (n === Infinity) return "Infinity";
  if (n === -Infinity) return "-Infinity";
  if (Number.isFinite(n) && Math.abs(n) > INFINITY_THRESHOLD) return n > 0 ? "Infinity" : "-Infinity";
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function formatPctOfMarginRequired(amountRaw, marginRaw) {
  const margin = parseScripNumeric(marginRaw);
  if (!Number.isFinite(margin) || margin <= 0) return "—";

  const amount = parseScripNumeric(amountRaw);
  if (amount === Infinity || (Number.isFinite(amount) && amount > INFINITY_THRESHOLD)) {
    return "+Infinity%";
  }
  if (amount === -Infinity || (Number.isFinite(amount) && amount < -INFINITY_THRESHOLD)) {
    return "-Infinity%";
  }
  if (Number.isNaN(amount)) return "—";

  const pct = (amount / margin) * 100;
  const absFormatted = Math.abs(pct).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const sign = pct >= 0 ? "+" : "-";
  return `${sign}${absFormatted}%`;
}

export const SCRIPT_FIELD_TO_API = {
  expiry: "expiry_date",
  scripType: "segment",
  tradingSymbol: "symbol",
  category: "option_type",
  strike: "strike_price",
};

export function getApiFieldForScriptSelection(field) {
  return SCRIPT_FIELD_TO_API[field] ?? field;
}

const SCRIP_DIMENSION_API_FIELDS = new Set([
  "exchange",
  "segment",
  "symbol",
  "option_type",
  "strike_price",
  "expiry_date",
]);

export function isScripDimensionApiField(apiField) {
  return SCRIP_DIMENSION_API_FIELDS.has(apiField);
}

/** Qty/Lots: integer, minimum 1. Empty or invalid uses fallback (also floored, min 1). */
export function clampQtyLots(raw, fallbackLotSize = 1) {
  const fbRaw = Number(fallbackLotSize);
  const fb = Number.isFinite(fbRaw) ? Math.max(1, Math.floor(fbRaw)) : 1;
  const t = raw != null ? String(raw).trim() : "";
  if (t === "") return fb;
  const n = Number(t);
  if (!Number.isFinite(n)) return fb;
  return Math.max(1, Math.floor(n));
}

export function deriveAbsDisclosedQty(qtyLotsStr, exchange, lotSize) {
  const t = qtyLotsStr != null ? String(qtyLotsStr).trim() : "";
  if (t === "") return "";
  const base = clampQtyLots(t, 1);
  const ex = String(exchange ?? "").trim().toUpperCase();
  if (ex === "NFO") {
    const ls = Number(lotSize);
    if (!Number.isFinite(ls) || ls <= 0) return "";
    return String(Math.max(0, Math.round(base * ls)));
  }
  return String(Math.max(0, Math.round(base * 0.1)));
}

export function scripInfoInputQuantity(qtyLotsStr, exchange) {
  const ex = String(exchange ?? "").trim().toUpperCase();
  if (ex === "NFO") {
    return qtyLotsStr != null ? String(qtyLotsStr).trim() : "";
  }
  return String(clampQtyLots(qtyLotsStr, 1));
}

export function orderQuantityFromQtyLotsField(qtyLotsStr, exchange, lotSize) {
  const lotsOrQty = clampQtyLots(qtyLotsStr, 1);
  if (String(exchange ?? "").trim().toUpperCase() === "NFO") {
    const ls = Number(lotSize);
    if (!Number.isFinite(ls) || ls <= 0) return lotsOrQty;
    return Math.max(1, Math.round(lotsOrQty * ls));
  }
  return lotsOrQty;
}

export function mapOrderInitiationScripInfoResponse(data) {
  if (!data || (data.status !== true && data.Status !== true)) return null;
  const strategy = data.strategy_builder_info || data.StrategyInfo || {};
  const margin = data.margin_info || data.MarginInfo || {};
  return {
    Status: data.status === true || data.Status === true,
    StrategyInfo: {
      MaxProfit: strategy.max_profit ?? strategy.MaxProfit,
      MaxLoss: strategy.max_loss ?? strategy.MaxLoss,
      BreakevenList: strategy.breakeven_list ?? strategy.BreakevenList ?? [],
      TotalPremium: strategy.total_premium ?? strategy.TotalPremium,
      PremiumPerLot: strategy.premium_per_lot ?? strategy.PremiumPerLot,
      RewardRisk: strategy.reward_risk ?? strategy.RewardRisk ?? strategy.profit_probability ?? "—",
    },
    MarginInfo: {
      InitialMarginRequired: margin.initial_margin_required ?? margin.InitialMarginRequired,
    },
  };
}

export const EMPTY_SCRIPT_SELECTION = {
  exchange: "",
  segment: "",
  symbol: "",
  option_type: "",
  strike_price: "",
  expiry_date: "",
  transactionType: "Buy",
  qtyLots: "1",
  absDisQty: "",
  productType: "",
  orderType: "",
  price: "",
  validity: "",
  amType: "",
  amPrice: "",
  sweeping: false,
  autoModify: false,
  strategy: "",
};
